import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  FaClipboardList, FaUser, FaEnvelope, FaClock, FaCheckCircle, 
  FaExclamationTriangle, FaSync, FaPaperPlane, FaEye, FaLink,
  FaWeight, FaRuler, FaBullseye, FaRunning, FaUtensils, FaHeartbeat,
  FaBriefcase, FaAllergies, FaCalendarAlt, FaSpinner, FaSearch,
  FaFilter, FaTrash
} from 'react-icons/fa';

const API = process.env.REACT_APP_BACKEND_URL;

const PendingPlansManager = () => {
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState({ pending: 0, in_progress: 0, completed: 0, sent: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [sendLoading, setSendLoading] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/personalized-plans/all`, { withCredentials: false });
      setPlans(response.data.plans || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Error al cargar los planes');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsViewed = async (planId) => {
    try {
      await axios.put(`${API}/api/personalized-plans/${planId}/mark-viewed`, {}, { withCredentials: false });
      fetchPlans();
    } catch (error) {
      console.error('Error marking as viewed:', error);
    }
  };

  const handleUpdateStatus = async (planId, newStatus) => {
    try {
      await axios.put(`${API}/api/personalized-plans/${planId}/update-status?status=${newStatus}`, {}, { withCredentials: false });
      toast.success(`Estado actualizado a: ${getStatusLabel(newStatus)}`);
      fetchPlans();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error al actualizar el estado');
    }
  };

  const handleSendToUser = async () => {
    if (!selectedPlan) return;
    
    setSendLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/personalized-plans/${selectedPlan.id}/send-to-user`,
        {
          plan_id: selectedPlan.id,
          send_email: true,
          custom_message: customMessage || null
        },
        { withCredentials: false }
      );
      
      if (response.data.success) {
        toast.success('¡Plan enviado al usuario exitosamente!');
        setShowSendDialog(false);
        setCustomMessage('');
        fetchPlans();
      }
    } catch (error) {
      console.error('Error sending plan:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar el plan');
    } finally {
      setSendLoading(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('¿Estás segura de eliminar este plan? Esta acción no se puede deshacer.')) return;
    
    try {
      await axios.delete(`${API}/api/personalized-plans/${planId}`, { withCredentials: false });
      toast.success('Plan eliminado');
      fetchPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Error al eliminar el plan');
    }
  };

  const openDetailDialog = (plan) => {
    setSelectedPlan(plan);
    setShowDetailDialog(true);
    if (plan.is_new) {
      handleMarkAsViewed(plan.id);
    }
  };

  const openSendDialog = (plan) => {
    setSelectedPlan(plan);
    setShowSendDialog(true);
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      in_progress: 'En Progreso',
      completed: 'Completado',
      sent: 'Enviado'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      in_progress: 'bg-blue-500',
      completed: 'bg-green-500',
      sent: 'bg-purple-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const filteredPlans = plans.filter(plan => {
    const matchesFilter = filter === 'all' || plan.status === filter;
    const matchesSearch = 
      plan.client_data?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.client_data?.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getObjectiveLabel = (objective) => {
    const labels = {
      perder_grasa: 'Perder grasa',
      ganar_musculo: 'Ganar músculo',
      mantener: 'Mantener peso',
      definicion: 'Definición muscular',
      competencia: 'Competencia',
      recomposicion: 'Recomposición'
    };
    return labels[objective] || objective;
  };

  const getActivityLabel = (level) => {
    const labels = {
      sedentario: 'Sedentario',
      ligero: 'Ligeramente activo',
      moderado: 'Moderadamente activo',
      activo: 'Muy activo',
      muy_activo: 'Extra activo'
    };
    return labels[level] || level;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FaClipboardList className="text-pink-500" /> Planes Personalizados Pendientes
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Gestiona los planes de usuarios Premium, Gold y Competitivo
          </p>
        </div>
        <Button onClick={fetchPlans} variant="outline">
          <FaSync className={loading ? 'animate-spin mr-2' : 'mr-2'} /> Actualizar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className={`cursor-pointer transition-all hover:scale-105 ${
          stats.pending > 0 ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-300 animate-pulse' : 'bg-yellow-50 border-yellow-200'
        }`} onClick={() => setFilter('pending')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${stats.pending > 0 ? 'text-red-600' : 'text-yellow-600'}`}>
                  NUEVOS / Pendientes
                </p>
                <p className={`text-3xl font-bold ${stats.pending > 0 ? 'text-red-700' : 'text-yellow-700'}`}>
                  {stats.pending}
                </p>
              </div>
              <FaExclamationTriangle className={`text-4xl ${stats.pending > 0 ? 'text-red-400' : 'text-yellow-300'}`} />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-50 border-blue-200 cursor-pointer hover:scale-105 transition-all" onClick={() => setFilter('in_progress')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">En Progreso</p>
                <p className="text-3xl font-bold text-blue-700">{stats.in_progress}</p>
              </div>
              <FaSync className="text-4xl text-blue-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200 cursor-pointer hover:scale-105 transition-all" onClick={() => setFilter('completed')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Completados</p>
                <p className="text-3xl font-bold text-green-700">{stats.completed}</p>
              </div>
              <FaCheckCircle className="text-4xl text-green-300" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-purple-50 border-purple-200 cursor-pointer hover:scale-105 transition-all" onClick={() => setFilter('sent')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600">Enviados</p>
                <p className="text-3xl font-bold text-purple-700">{stats.sent}</p>
              </div>
              <FaPaperPlane className="text-4xl text-purple-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* FIT EVOLUTION: banner rojo global con no-liberados */}
      {(stats.not_released > 0 || stats.ready_to_auto_release > 0) && (
        <Card className="bg-red-50 border-red-500 border-2" data-testid="fe-not-released-banner">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FaExclamationTriangle className="text-3xl text-red-500 animate-pulse" />
                <div>
                  <p className="text-lg font-bold text-red-700">
                    {stats.not_released} plan(es) Fit Evolution pendientes de liberar
                  </p>
                  <p className="text-sm text-red-600">
                    Incluye nuevos y actualizaciones. El sistema los publicará automáticamente al cumplir 24h.
                    {stats.ready_to_auto_release > 0 && (
                      <> <strong className="text-emerald-700">{stats.ready_to_auto_release} listo(s) para auto-liberar en el próximo ciclo (15 min).</strong></>
                    )}
                  </p>
                </div>
              </div>
              <Button
                onClick={fetchPlans}
                variant="outline"
                size="sm"
                className="border-red-500 text-red-700 hover:bg-red-100"
                data-testid="fe-refresh-not-released"
              >
                <FaSync className="mr-1" /> Actualizar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => setFilter('all')}
                size="sm"
              >
                Todos
              </Button>
              <Button
                variant={filter === 'pending' ? 'default' : 'outline'}
                onClick={() => setFilter('pending')}
                size="sm"
                className={filter === 'pending' ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
              >
                Pendientes
              </Button>
              <Button
                variant={filter === 'in_progress' ? 'default' : 'outline'}
                onClick={() => setFilter('in_progress')}
                size="sm"
                className={filter === 'in_progress' ? 'bg-blue-500 hover:bg-blue-600' : ''}
              >
                En Progreso
              </Button>
              <Button
                variant={filter === 'completed' ? 'default' : 'outline'}
                onClick={() => setFilter('completed')}
                size="sm"
                className={filter === 'completed' ? 'bg-green-500 hover:bg-green-600' : ''}
              >
                Completados
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plans List */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Planes ({filteredPlans.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <FaSpinner className="animate-spin text-4xl text-pink-500 mx-auto mb-2" />
              <p className="text-gray-500">Cargando planes...</p>
            </div>
          ) : filteredPlans.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <FaClipboardList className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No hay planes</h3>
              <p className="text-gray-500">No se encontraron planes con los filtros actuales</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredPlans.map((plan) => {
                const client = plan.client_data || {};
                const isNew = plan.is_new;
                const hoursRemaining = plan.hours_remaining || 0;
                const isOverdue = plan.is_overdue || hoursRemaining < 0;
                const isUrgent = hoursRemaining < 12 && !isOverdue && plan.status === 'pending';
                // FIT EVOLUTION: bandera roja para todo lo NO liberado (nuevo + actualización)
                const fe = plan.fit_evolution || null;
                const isFEBasic = fe?.is_basic;
                const isNotReleased = plan.status !== 'sent';
                const isUpdateNotReleased = fe?.is_update_pending_release;
                const feHoursUntilAuto = fe?.hours_until_auto_release;
                const feCanAutoRelease = fe?.can_auto_release;
                const showFERedFlag = isFEBasic && isNotReleased;

                return (
                  <div
                    key={plan.id}
                    className={`border rounded-lg p-4 transition-all hover:shadow-md ${
                      showFERedFlag ? 'bg-red-50 border-red-500 border-2 ring-2 ring-red-200' :
                      isNew ? 'bg-red-50 border-red-300 border-2' :
                      isOverdue ? 'bg-orange-50 border-orange-300' :
                      isUrgent ? 'bg-yellow-50 border-yellow-300' :
                      'bg-white'
                    }`}
                    data-testid={showFERedFlag ? `fe-not-released-${plan.id}` : `plan-row-${plan.id}`}
                  >
                    {/* BANDERA ROJA Fit Evolution: aviso con horas restantes */}
                    {showFERedFlag && (
                      <div className="mb-3 flex items-center justify-between p-3 rounded-md bg-red-100 border border-red-300">
                        <div className="flex items-center gap-2 text-red-800 text-sm font-semibold">
                          <FaExclamationTriangle className="text-red-600" />
                          {isUpdateNotReleased ? 'Actualización pendiente de liberar' : 'Plan pendiente de liberar'}
                        </div>
                        {feHoursUntilAuto !== null && feHoursUntilAuto !== undefined && (
                          <div className="text-right">
                            {feCanAutoRelease ? (
                              <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded" data-testid={`fe-auto-ready-${plan.id}`}>
                                ⏱️ El sistema lo publicará en el próximo ciclo (auto 15min)
                              </span>
                            ) : (
                              <span className="text-xs font-bold text-red-700" data-testid={`fe-hours-left-${plan.id}`}>
                                🕒 Faltan <strong>{feHoursUntilAuto}h</strong> para auto-liberar (no publiques antes)
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      {/* Info principal */}
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          isNew ? 'bg-red-500' : 'bg-pink-100'
                        }`}>
                          {isNew ? (
                            <span className="text-white font-bold text-xs">NUEVO</span>
                          ) : (
                            <FaUser className="text-pink-500 text-xl" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{client.full_name || 'Sin nombre'}</h3>
                            <Badge className={getStatusColor(plan.status)}>
                              {getStatusLabel(plan.status)}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                              {client.plan_type || 'premium'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <FaEnvelope className="text-gray-400" /> {client.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <FaCalendarAlt className="text-gray-400" /> {formatDate(plan.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Tiempo restante y acciones */}
                      <div className="flex items-center gap-4">
                        {plan.status === 'pending' && (
                          <div className={`text-right ${isOverdue ? 'text-red-600' : isUrgent ? 'text-orange-600' : 'text-gray-600'}`}>
                            <p className="text-xs">Tiempo restante</p>
                            <p className="font-bold">
                              {isOverdue ? 'VENCIDO' : `${Math.round(hoursRemaining)}h`}
                            </p>
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDetailDialog(plan)}
                            title="Ver detalles"
                          >
                            <FaEye />
                          </Button>
                          
                          {plan.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-blue-600 hover:bg-blue-50"
                              onClick={() => handleUpdateStatus(plan.id, 'in_progress')}
                              title="Marcar en progreso"
                            >
                              <FaSync />
                            </Button>
                          )}
                          
                          {plan.status === 'in_progress' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => handleUpdateStatus(plan.id, 'completed')}
                              title="Marcar como completado"
                            >
                              <FaCheckCircle />
                            </Button>
                          )}
                          
                          {plan.status === 'completed' && (
                            <Button
                              size="sm"
                              className="bg-gradient-to-r from-pink-500 to-purple-500 text-white"
                              onClick={() => openSendDialog(plan)}
                              title="Publicar Plan Fit Evolution (Dieta + Rutina)"
                              data-testid="release-fit-evolution-plan-btn"
                            >
                              <FaPaperPlane className="mr-1" /> Publicar Fit Evolution
                            </Button>
                          )}
                          
                          {/* Botón de eliminar para TODOS los estados incluyendo enviados */}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeletePlan(plan.id)}
                            title="Eliminar"
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Quick info */}
                    <div className="mt-3 flex gap-4 text-xs text-gray-500">
                      <span><strong>Objetivo:</strong> {getObjectiveLabel(client.objective)}</span>
                      <span><strong>Peso:</strong> {client.weight} kg</span>
                      <span><strong>Altura:</strong> {client.height} cm</span>
                      <span><strong>Comidas:</strong> {client.meals_per_day}/día</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FaUser className="text-pink-500" /> 
              Detalles del Plan - {selectedPlan?.client_data?.full_name}
            </DialogTitle>
            <DialogDescription>
              Información completa del formulario del cliente
            </DialogDescription>
          </DialogHeader>
          
          {selectedPlan && (
            <div className="space-y-6">
              {/* Info básica */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FaUser className="text-gray-400" /> Información Personal
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div><strong>Nombre:</strong> {selectedPlan.client_data?.full_name}</div>
                    <div><strong>Email:</strong> {selectedPlan.client_data?.email}</div>
                    <div><strong>Edad:</strong> {selectedPlan.client_data?.age} años</div>
                    <div><strong>Peso:</strong> {selectedPlan.client_data?.weight} kg</div>
                    <div><strong>Altura:</strong> {selectedPlan.client_data?.height} cm</div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FaBullseye className="text-gray-400" /> Objetivos
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div><strong>Objetivo:</strong> {getObjectiveLabel(selectedPlan.client_data?.objective)}</div>
                    <div><strong>Actividad:</strong> {getActivityLabel(selectedPlan.client_data?.activity_level)}</div>
                    <div><strong>Tipo trabajo:</strong> {selectedPlan.client_data?.work_type === 'sedentario' ? 'Sedentario' : 'Activo'}</div>
                    <div><strong>Plan:</strong> <Badge>{selectedPlan.client_data?.plan_type}</Badge></div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Horarios y alimentación */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FaClock className="text-gray-400" /> Horarios
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div><strong>Se levanta:</strong> {selectedPlan.client_data?.wake_up_time}</div>
                    <div><strong>Se acuesta:</strong> {selectedPlan.client_data?.sleep_time}</div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FaUtensils className="text-gray-400" /> Alimentación
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div><strong>Comidas/día:</strong> {selectedPlan.client_data?.meals_per_day}</div>
                    <div><strong>Post-entreno:</strong> {selectedPlan.client_data?.post_workout_meal}</div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Alergias y patologías */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FaHeartbeat className="text-gray-400" /> Salud y Preferencias
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <strong>Alergias:</strong>{' '}
                    {selectedPlan.client_data?.food_allergies?.length > 0 ? (
                      selectedPlan.client_data.food_allergies.map((a, i) => (
                        <Badge key={i} variant="destructive" className="mr-1">{a}</Badge>
                      ))
                    ) : (
                      <span className="text-gray-500">Ninguna</span>
                    )}
                  </div>
                  <div>
                    <strong>Alimentos que no le gustan:</strong>{' '}
                    {selectedPlan.client_data?.disliked_foods?.length > 0 
                      ? selectedPlan.client_data.disliked_foods.join(', ')
                      : <span className="text-gray-500">Ninguno especificado</span>
                    }
                  </div>
                  <div>
                    <strong>Patologías:</strong>{' '}
                    {selectedPlan.client_data?.pathologies?.length > 0 ? (
                      selectedPlan.client_data.pathologies.map((p, i) => (
                        <Badge key={i} variant="outline" className="mr-1 capitalize">{p.replace(/_/g, ' ')}</Badge>
                      ))
                    ) : (
                      <span className="text-gray-500">Ninguna</span>
                    )}
                  </div>
                  <div>
                    <strong>Conducta alimentaria:</strong>{' '}
                    {selectedPlan.client_data?.eating_disorder_option === 'videollamada' ? (
                      <Badge className="bg-purple-500">Solicita videollamada</Badge>
                    ) : (
                      <span className="text-gray-500">Sin problemas reportados</span>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Estado y acciones */}
              <Card className="bg-gray-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-gray-500">Estado actual:</span>
                      <Badge className={`ml-2 ${getStatusColor(selectedPlan.status)}`}>
                        {getStatusLabel(selectedPlan.status)}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      {selectedPlan.status === 'pending' && (
                        <Button
                          onClick={() => {
                            handleUpdateStatus(selectedPlan.id, 'in_progress');
                            setShowDetailDialog(false);
                          }}
                          className="bg-blue-500 hover:bg-blue-600"
                        >
                          <FaSync className="mr-2" /> Iniciar Trabajo
                        </Button>
                      )}
                      {selectedPlan.status === 'in_progress' && (
                        <Button
                          onClick={() => {
                            handleUpdateStatus(selectedPlan.id, 'completed');
                            setShowDetailDialog(false);
                          }}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          <FaCheckCircle className="mr-2" /> Marcar Completado
                        </Button>
                      )}
                      {selectedPlan.status === 'completed' && (
                        <Button
                          onClick={() => {
                            setShowDetailDialog(false);
                            openSendDialog(selectedPlan);
                          }}
                          className="bg-gradient-to-r from-pink-500 to-purple-500"
                        >
                          <FaPaperPlane className="mr-2" /> Enviar al Usuario
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FaPaperPlane className="text-pink-500" /> Publicar Fit Evolution
            </DialogTitle>
            <DialogDescription>
              Al confirmar liberarás simultáneamente el <strong>Plan de Alimentación</strong> y la
              <strong> Rutina</strong> de la clienta. Recibirá una notificación in-app y un email para
              entrar a su dashboard.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                <strong>Usuario:</strong> {selectedPlan?.client_data?.full_name}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Email:</strong> {selectedPlan?.client_data?.email}
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium">Mensaje personalizado (opcional)</label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Escribe un mensaje especial para incluir en el email..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSendToUser}
              disabled={sendLoading}
              className="bg-gradient-to-r from-pink-500 to-purple-500"
              data-testid="confirm-release-fit-evolution-btn"
            >
              {sendLoading ? (
                <>
                  <FaSpinner className="animate-spin mr-2" /> Publicando...
                </>
              ) : (
                <>
                  <FaPaperPlane className="mr-2" /> Publicar Dieta + Rutina
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingPlansManager;
